<!--
  document-upload.component.html
  --------------------------------
  Module 4 - Document Upload page.

  Supports two modes:
    NEW  - standard first-visit (isEditMode = false)
    EDIT - back-navigation from a later step (isEditMode = true)
           Pre-populates all fields and shows already-uploaded files.
-->

<div class="upload-page">

  <!-- Loading overlay — shown while fetching existing session OR uploading to blob -->
  <div class="upload-page__loading-overlay" *ngIf="isLoadingSession || isSubmitting" aria-live="polite">

    <!-- Spinning circle (same SVG as category-review) -->
    <div class="upload-page__spinner-wrap">
      <svg class="upload-page__spinner" viewBox="0 0 56 56" fill="none"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <!-- Track -->
        <circle cx="28" cy="28" r="24"
                stroke="#E6E6E6" stroke-width="5"/>
        <!-- Animated arc -->
        <circle cx="28" cy="28" r="24"
                stroke="url(#uploadSpinGrad)" stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="150.8"
                stroke-dashoffset="37.7"
                class="upload-page__spinner-arc"/>
        <defs>
          <linearGradient id="uploadSpinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stop-color="#0056A2"/>
            <stop offset="100%" stop-color="#19E3C5"/>
          </linearGradient>
        </defs>
      </svg>
    </div>

    <!-- Indeterminate progress bar (no real % — pulse animation) -->
    <div class="upload-page__loader-bar-row">
      <div class="upload-page__loader-bar">
        <div class="upload-page__loader-bar__fill"></div>
      </div>
    </div>

    <!-- Status message -->
    <ng-container *ngIf="isSubmitting; else loadingMsg">
      <p class="upload-page__loader-msg">
        {{ isEditMode ? '🔄 Saving your changes…' : '📤 Uploading your documents…' }}
      </p>
    </ng-container>
    <ng-template #loadingMsg>
      <p class="upload-page__loader-msg">⏳ Loading your session…</p>
    </ng-template>

  </div><!-- /upload-page__loading-overlay -->

  <!-- Page Header -->
  <header class="upload-page__header">

    <h1 class="upload-page__title">
      <ng-container *ngIf="isEditMode; else newTitle">
        Edit Document Upload
      </ng-container>
      <ng-template #newTitle>Document Upload</ng-template>
    </h1>

    <p class="upload-page__subtitle" *ngIf="isEditMode">
      Review and update your application information and documents, then click
      <strong>Upload &amp; Continue</strong> to save your changes.
    </p>
    <p class="upload-page__subtitle" *ngIf="!isEditMode">
      Complete the mandatory fields below and upload your documents for automated guardrail analysis.
    </p>

  </header>

  <form class="upload-form" (ngSubmit)="onSubmit()" #uploadForm="ngForm" novalidate>

    <!-- Application Information Card -->
    <section class="upload-page__card">

      <h2 class="upload-page__section-title">Application Information</h2>
      <p class="upload-page__section-sub">
        Please provide the following information before uploading documents:
      </p>

      <div class="upload-page__form-body">

      <!-- Row: Analyst / Engineer Name -->
      <div class="form-row">
        <div class="form-field">
          <label class="form-label" for="analystName">
            Analyst/Engineer Name
            <span class="required">*</span>
            <span class="form-hint">(auto-filled from Entra ID)</span>
          </label>
          <input
            id="analystName"
            name="analystName"
            type="text"
            class="form-input"
            [(ngModel)]="form.analystName"
            placeholder="e.g. John Smith"
            required
            readonly />
        </div>
      </div>

      <!-- Row: CAT ID | Application ID -->
      <div class="form-row form-row--cols-2">

        <div class="form-field">
          <label class="form-label" for="catId">
            CAT ID <span class="optional">(optional)</span>
          </label>
          <input
            id="catId"
            name="catId"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            class="form-input"
            [class.form-input--error]="catIdError"
            [(ngModel)]="form.catId"
            (input)="form.catId = sanitize(form.catId)"
            (blur)="onFieldBlur('catId')"
            placeholder="e.g. 0123456789"
            maxlength="10" />
          <span class="form-error" *ngIf="catIdError">{{ catIdError }}</span>
        </div>

        <div class="form-field">
          <label class="form-label" for="appId">
            Application ID <span class="required">*</span><span class="optional">(enter 0 if unavailable)</span>
          </label>
          <input
            id="appId"
            name="appId"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            class="form-input"
            [class.form-input--error]="appIdError"
            [(ngModel)]="form.appId"
            (input)="form.appId = sanitizeNumeric(form.appId, 10)"
            (blur)="onFieldBlur('appId')"
            placeholder="e.g. 0123456789"
            maxlength="10"
            required />
          <span class="form-error" *ngIf="appIdError">{{ appIdError }}</span>
        </div>

      </div>

      <!-- Row: Application Name | Management Style -->
      <div class="form-row form-row--cols-2">

        <div class="form-field">
          <label class="form-label" for="appName">
            Application Name <span class="required">*</span>
          </label>
          <input
            id="appName"
            name="appName"
            type="text"
            class="form-input"
            [class.form-input--error]="appNameError"
            [(ngModel)]="form.appName"
            (input)="form.appName = sanitize(form.appName)"
            (blur)="onFieldBlur('appName')"
            placeholder="Enter the application name"
            maxlength="100"
            required />
          <span class="form-error" *ngIf="appNameError">{{ appNameError }}</span>
        </div>

        <div class="form-field">
          <label class="form-label" for="managementStyle">
            Management Style <span class="required">*</span>
          </label>
          <select
            id="managementStyle"
            name="managementStyle"
            class="form-select"
            [(ngModel)]="form.managementStyle"
            required>
            <option value="" disabled selected>Select Style</option>
            <option value="COTS">COTS (Commercial Off-The-Shelf)</option>
            <option value="homegrown">Homegrown</option>
          </select>
        </div>

      </div>

      <!-- Row: Application Type (2) | Tier (1) | SL Level (1) -->
      <div class="form-row form-row--cols-211">

        <!-- Application Type multi-checkbox dropdown -->
        <div class="form-field">
          <label class="form-label">
            Application Type <span class="required">*</span>
          </label>

          <div
            class="checkbox-dropdown"
            [class.checkbox-dropdown--open]="appTypeDropdownOpen">

            <button
              type="button"
              class="checkbox-dropdown__trigger"
              (click)="appTypeDropdownOpen = !appTypeDropdownOpen"
              [class.checkbox-dropdown__trigger--placeholder]="appTypeLabel === 'Select Type(s)'">
              <span>{{ appTypeLabel }}</span>
              <span class="checkbox-dropdown__arrow">&#9660;</span>
            </button>

            <div class="checkbox-dropdown__panel" *ngIf="appTypeDropdownOpen">
              <label class="checkbox-option" *ngFor="let opt of appTypeOptions">
                <input
                  type="checkbox"
                  [value]="opt.value"
                  [checked]="form.appTypes.includes(opt.value)"
                  (change)="onAppTypeToggle(opt.value)" />
                <span>{{ opt.label }}</span>
              </label>
            </div>

          </div>
        </div>

        <!-- Tier -->
        <div class="form-field">
          <label class="form-label" for="tier">
            Tier <span class="required">*</span>
          </label>
          <select
            id="tier"
            name="tier"
            class="form-select"
            [(ngModel)]="form.tier"
            required>
            <option value="" disabled selected>Select Tier</option>
            <option value="tier1">Tier 1</option>
            <option value="tier2">Tier 2</option>
            <option value="tier3">Tier 3</option>
          </select>
        </div>

        <!-- SL Level - enabled only when OT is selected -->
        <div class="form-field">
          <label
            class="form-label"
            for="slLevel"
            [class.form-label--disabled]="!isOtSelected">
            SL Level <span class="required" *ngIf="isOtSelected">*</span>
            <span class="form-hint" *ngIf="!isOtSelected">(OT only)</span>
          </label>
          <select
            id="slLevel"
            name="slLevel"
            class="form-select"
            [(ngModel)]="form.slLevel"
            [disabled]="!isOtSelected">
            <option value="" disabled selected>Select SL Level</option>
            <option value="SL1">SL1</option>
            <option value="SL2">SL2</option>
            <option value="SL3">SL3</option>
            <option value="SL4">SL4</option>
          </select>
        </div>

      </div>

      <!-- Row: Architecture Diagram or Description -->
      <div class="form-row">
        <div class="form-field">
          <label class="form-label">
            Architecture Diagram or Description <span class="required">*</span>
          </label>

          <div class="arch-split">

            <!-- Left: file dropzone -->
            <div class="arch-split__dropzone">
              <div
                class="dropzone dropzone--small"
                [class.dropzone--dragover]="archDragOver"
                (dragover)="onDragOver($event, 'arch')"
                (dragleave)="onDragLeave('arch')"
                (drop)="onDrop($event, 'arch')"
                (click)="archFileInput.click()">
                <img class="dropzone__svg-icon dropzone__svg-icon--small" src="cvx_document_icon_RGB_FA.svg" alt="" aria-hidden="true" draggable="false" />
                <span class="dropzone__label">
                  Drag &amp; drop file here or <span class="dropzone__browse">browse</span>
                </span>
                <span class="dropzone__sub">{{ ARCH_ALLOWED_LABEL }} &mdash; max {{ MAX_FILE_SIZE_LABEL }}</span>
                <input
                  #archFileInput
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.bmp,.tiff,.tif"
                  hidden
                  (change)="onArchFileSelected($event)" />
              </div>
              <!-- Selected / new architecture file chip -->
              <div class="file-chip" [class.file-chip--unsupported]="isArchFileNonAscii" *ngIf="archFile">
                <span class="file-chip__name" [title]="archFile.name">{{ archFile.name }}</span>
                <span class="file-chip__tag" *ngIf="isArchFileNonAscii">Invalid name</span>
                <button type="button" class="file-chip__remove" (click)="removeArchFile()" aria-label="Remove file">&times;</button>
              </div>
              <p class="field-error" *ngIf="archFile && isArchFileNonAscii" role="alert">
                This file name contains non-ASCII characters and will not be uploaded. Please rename it using
                only standard English letters, numbers, spaces, and common punctuation, then re-select it.
              </p>
              <!-- Rejected (unsupported type / too large) architecture file chip -->
              <div class="file-chip file-chip--unsupported" *ngIf="archFileRejected">
                <span class="file-chip__name" [title]="archFileRejected.name">{{ archFileRejected.name }}</span>
                <span class="file-chip__tag">{{ archFileRejectedReason === 'size' ? 'Too large' : 'Unsupported' }}</span>
                <button type="button" class="file-chip__remove" (click)="removeArchFile()" aria-label="Remove file">&times;</button>
              </div>
              <p class="field-error" *ngIf="archFileRejected" role="alert">
                <ng-container *ngIf="archFileRejectedReason === 'size'">
                  This file is larger than {{ MAX_FILE_SIZE_LABEL }} and will not be uploaded, please select a smaller file.
                </ng-container>
                <ng-container *ngIf="archFileRejectedReason !== 'size'">
                  This file will not be uploaded, please select a supported file.
                </ng-container>
              </p>
            </div>

            <!-- Vertical OR divider -->
            <div class="arch-split__or">
              <span>or</span>
            </div>

            <!-- Right: description textarea -->
            <div class="arch-split__textarea">
              <textarea
                id="archDescription"
                name="archDescription"
                class="form-textarea arch-split__text"
                [class.form-input--error]="archError"
                [(ngModel)]="form.archDescription"
                (input)="form.archDescription = sanitize(form.archDescription)"
                (blur)="onFieldBlur('archDescription')"
                maxlength="2000"
                placeholder="Describe the application architecture, components, technologies used, and system design...">
              </textarea>
            </div>

          </div><!-- /.arch-split -->
          <span class="form-error" *ngIf="archError">{{ archError }}</span>
        </div>
      </div>

      <!-- Row: Additional Application Information -->
      <div class="form-row">
        <div class="form-field">
          <label class="form-label" for="additionalInfo">
            Additional Application Information
          </label>
          <textarea
            id="additionalInfo"
            name="additionalInfo"
            class="form-textarea"
            [(ngModel)]="form.additionalInfo"
            (input)="form.additionalInfo = sanitize(form.additionalInfo)"
            rows="3"
            maxlength="1000"
            placeholder="Any additional context, notes, or relevant information about the application...">
          </textarea>
        </div>
      </div>

      </div><!-- /.upload-page__form-body -->

    </section>

    <!-- Document Upload Card -->
    <section class="upload-page__card upload-page__card--upload">

      <h2 class="upload-page__section-title">Document Upload</h2>
      <p class="upload-page__section-sub">
        <ng-container *ngIf="isEditMode">
          Add new documents or remove existing ones. Changes are applied when you click
          <strong>Upload &amp; Continue</strong>.
        </ng-container>
        <ng-container *ngIf="!isEditMode">
          Upload application artifacts for guardrail analysis.
        </ng-container>
      </p>

      <div class="upload-page__form-body">

      <!-- Artifacts Dropzone -->
      <div class="form-row">
        <div class="form-field">
          <label class="form-label">Artifacts</label>
          <div
            class="dropzone dropzone--hero"
            [class.dropzone--dragover]="artifactsDragOver"
            (dragover)="onDragOver($event, 'artifacts')"
            (dragleave)="onDragLeave('artifacts')"
            (drop)="onDrop($event, 'artifacts')">

            <img class="dropzone__svg-icon" src="cvx_document_icon_RGB_FA.svg" alt="" aria-hidden="true" draggable="false" />

            <p class="dropzone__title">Drag and drop your files here</p>
            <span class="dropzone__or">or</span>

            <button
              type="button"
              class="dropzone__browse-btn"
              (click)="artifactsFileInput.click()">
              Browse Files
            </button>

            <span class="dropzone__sub">
              {{ ARTIFACT_ALLOWED_LABEL }} &mdash; max {{ MAX_FILE_SIZE_LABEL }} each, up to {{ MAX_FILES }} files
            </span>

            <input
              #artifactsFileInput
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff"
              multiple
              hidden
              (change)="onArtifactsSelected($event)" />
          </div>

          <!-- File count pill — shown below the dropzone once files are selected -->
          <div class="dropzone__file-count" *ngIf="artifactFiles.length > 0" aria-live="polite">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207z" clip-rule="evenodd"/>
            </svg>
            {{ artifactFiles.length }} file{{ artifactFiles.length !== 1 ? 's' : '' }} selected
            — scroll down to review or remove them
          </div>
        </div>
      </div>

      <!-- Number of Webpage Links -->
      <div class="form-row form-row--link-count">
        <div class="form-field form-field--half">
          <label class="form-label" for="linkCount">
            Number of Webpage Links
            <span class="form-hint">(0-10)</span>
          </label>
          <div class="slider-row">
            <input
              id="linkCount"
              name="linkCount"
              type="range"
              class="form-slider"
              min="0"
              max="10"
              step="1"
              [ngModel]="form.linkCount"
              (ngModelChange)="onLinkCountChange($event)" />
            <span class="slider-value">{{ form.linkCount }}</span>
          </div>
        </div>
      </div>

      <!-- Webpage Link Inputs (dynamic) -->
      <div class="form-row form-row--links" *ngIf="form.linkCount > 0">
        <div class="form-field">
          <label class="form-label">Webpage Links</label>
          <div
            class="link-input-row"
            *ngFor="let link of form.links; let i = index; trackBy: trackByIndex">
            <span class="link-input-index">{{ i + 1 }}</span>
            <div class="link-input-field">
              <input
                [id]="'link_' + i"
                [name]="'link_' + i"
                type="url"
                class="form-input"
                [class.form-input--error]="!!linkError(link.value)"
                [(ngModel)]="link.value"
                (input)="link.value = sanitize(link.value)"
                maxlength="500"
                placeholder="https://www.example.com/page" />
              <span class="form-error" *ngIf="linkError(link.value)" role="alert">{{ linkError(link.value) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Blocked-reduce warning -->
      <div class="link-warning" *ngIf="linkReduceWarning">
        <span class="link-warning__icon">&#9888;</span>
        Clear the filled link(s) before reducing the count.
      </div>

      </div><!-- /.upload-page__form-body -->

    </section>

    <!-- Uploaded Documents Card -->
    <section class="upload-page__card">

      <div class="doc-list__header">
        <h2 class="upload-page__section-title doc-list__title">Uploaded Documents</h2>
        <!-- Badge shows count of visible (non-deleted) entries -->
        <span class="doc-list__badge">
          {{ visibleArtifacts.length }} file{{ visibleArtifacts.length !== 1 ? 's' : '' }}
        </span>
      </div>
      <hr class="doc-list__divider" />

      <!-- Pending-deletion notice (edit mode only) -->
      <div
        class="doc-list__delete-notice"
        *ngIf="isEditMode && pendingDeletionCount > 0">
        <span class="doc-list__delete-notice-icon">&#9888;</span>
        {{ pendingDeletionCount }} file{{ pendingDeletionCount !== 1 ? 's' : '' }}
        marked for removal. Changes will be applied when you click
        <strong>Upload &amp; Continue</strong>.
      </div>

      <!-- Too-many-files notice — shown when the attached count exceeds the limit -->
      <div class="doc-list__count-notice" *ngIf="hasTooManyFiles" role="alert">
        <span class="doc-list__count-notice-icon">&#9888;</span>
        Too many files — a maximum of {{ MAX_FILES }} files can be uploaded per session.
        You have {{ totalFileCount }}; please remove at least {{ totalFileCount - MAX_FILES }}
        file{{ (totalFileCount - MAX_FILES) !== 1 ? 's' : '' }} to continue.
      </div>

      <!-- Empty state -->
      <div class="doc-list__scroll-wrap" [class.doc-list__scroll-wrap--scrollable]="artifactFiles.length > 10">

      <div class="doc-list__empty" *ngIf="visibleArtifacts.length === 0">
        <svg class="doc-list__empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M6 16a4 4 0 0 1 4-4h12l5 6h27a4 4 0 0 1 4 4v26a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V16z" fill="#F5A623"/>
          <path d="M6 26h52v22a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V26z" fill="#F7BC5A"/>
        </svg>
        <p class="doc-list__empty-title">No documents uploaded yet</p>
        <p class="doc-list__empty-sub">Upload your first document to get started</p>
      </div>

      <!-- Document tiles - one per artifact (existing + new, excluding deleted) -->
      <ul class="doc-list" *ngIf="artifactFiles.length > 0">
        <li
          class="doc-tile"
          [class.doc-tile--deleting]="entry.markedForDeletion"
          [class.doc-tile--unsupported]="isUnsupportedEntry(entry) || isOversizedEntry(entry) || isNonAsciiEntry(entry)"
          *ngFor="let entry of artifactFiles; let i = index; trackBy: trackByIndex">

          <!-- File type icon -->
          <div class="doc-tile__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <rect x="4" y="1" width="18" height="24" rx="2" fill="#E0E7EF"/>
              <path d="M18 1v7h7" stroke="#B0BEC5" stroke-width="1.5" fill="none"/>
              <rect x="4" y="18" width="24" height="10" rx="2" fill="#0056A2"/>
            </svg>
          </div>

          <!-- File details -->
          <div class="doc-tile__info">
            <span
              class="doc-tile__name"
              [class.doc-tile__name--deleted]="entry.markedForDeletion"
              [title]="entry.displayName">
              {{ entry.displayName }}
            </span>
            <span class="doc-tile__meta">
              {{ getFileSize(entry.displaySize) }}
              <span class="doc-tile__source-badge" *ngIf="isEditMode">
                <ng-container *ngIf="entry.source === 'existing'">Saved</ng-container>
                <ng-container *ngIf="entry.source === 'new'">New</ng-container>
              </span>
            </span>
            <span class="doc-tile__unsupported-note" *ngIf="isUnsupportedEntry(entry)">
              &#9888; Unsupported file type, remove this file to continue
            </span>
            <span class="doc-tile__unsupported-note" *ngIf="!isUnsupportedEntry(entry) && isOversizedEntry(entry)">
              &#9888; Exceeds {{ MAX_FILE_SIZE_LABEL }} limit, remove this file to continue
            </span>
            <span class="doc-tile__unsupported-note" *ngIf="!isUnsupportedEntry(entry) && !isOversizedEntry(entry) && isNonAsciiEntry(entry)">
              &#9888; Invalid file name (non-ASCII characters), rename &amp; re-add this file
            </span>
          </div>

          <!-- Status badge -->
          <span
            class="doc-tile__status"
            [class.doc-tile__status--pending]="entry.status === 'pending' && !entry.markedForDeletion && !isUnsupportedEntry(entry) && !isOversizedEntry(entry) && !isNonAsciiEntry(entry)"
            [class.doc-tile__status--uploaded]="entry.status === 'uploaded' && !entry.markedForDeletion"
            [class.doc-tile__status--deleting]="entry.markedForDeletion"
            [class.doc-tile__status--unsupported]="isUnsupportedEntry(entry) || isOversizedEntry(entry) || isNonAsciiEntry(entry)">
            <ng-container *ngIf="entry.markedForDeletion">Pending Removal</ng-container>
            <ng-container *ngIf="!entry.markedForDeletion && isUnsupportedEntry(entry)">Unsupported</ng-container>
            <ng-container *ngIf="!entry.markedForDeletion && !isUnsupportedEntry(entry) && isOversizedEntry(entry)">Too large</ng-container>
            <ng-container *ngIf="!entry.markedForDeletion && !isUnsupportedEntry(entry) && !isOversizedEntry(entry) && isNonAsciiEntry(entry)">Invalid name</ng-container>
            <ng-container *ngIf="!entry.markedForDeletion && !isUnsupportedEntry(entry) && !isOversizedEntry(entry) && !isNonAsciiEntry(entry) && entry.status === 'pending'">Upload Pending</ng-container>
            <ng-container *ngIf="!entry.markedForDeletion && !isUnsupportedEntry(entry) && !isOversizedEntry(entry) && !isNonAsciiEntry(entry) && entry.status === 'uploaded'">Uploaded</ng-container>
          </span>

          <!-- Action buttons -->
          <div class="doc-tile__actions">
            <!-- Restore button (edit mode, existing file, marked for deletion) -->
            <button
              *ngIf="entry.markedForDeletion && entry.source === 'existing'"
              type="button"
              class="doc-tile__restore"
              (click)="restoreArtifact(i)"
              [attr.aria-label]="'Restore ' + entry.displayName">
              &#8635; Restore
            </button>

            <!-- Remove / mark-for-deletion button -->
            <button
              *ngIf="!entry.markedForDeletion"
              type="button"
              class="doc-tile__remove"
              (click)="removeArtifact(i)"
              [attr.aria-label]="'Remove ' + entry.displayName">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6 2h8M4 5h12M6 5v11a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                <path d="M8 9v5M12 9v5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

        </li>
      </ul>

      </div><!-- /doc-list__scroll-wrap -->

    </section>

    <!-- Error banner / Application-ID-zero warning -->
    <div
      class="upload-form__error"
      [class.upload-form__error--warning]="isAppIdZero && !submitError"
      *ngIf="submitError || isAppIdZero"
      role="alert">
      <svg class="upload-form__error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm.75-11.25a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0v-4.5zm-.75 7a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" clip-rule="evenodd"/>
      </svg>
      <ng-container *ngIf="submitError">{{ submitError }}</ng-container>
      <ng-container *ngIf="isAppIdZero && !submitError">
        The report will not be retrievable later because the Application ID is 0, but you can still continue with the assessment.
      </ng-container>
    </div>

    <!-- ── Required-fields hint — visible only while the submit button is disabled ── -->
    <div
      class="upload-form__required-hint"
      *ngIf="!isFormReady && !isSubmitting && !isLoadingSession"
      role="note"
      aria-live="polite">
      <svg class="upload-form__required-hint__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z" clip-rule="evenodd"/>
      </svg>
      <div class="upload-form__required-hint__body">
        <strong>Complete the following to enable Upload &amp; Continue:</strong>
        <ul class="upload-form__required-hint__list">
          <li *ngFor="let reason of disabledReasons">{{ reason }}</li>
        </ul>
      </div>
    </div>

    <!-- ── Form action buttons (centred) ─────────────────────────────── -->
    <div class="upload-form__actions">

      <!-- Clear All button — same ghost style as Back/Cancel buttons -->
      <button
        type="button"
        class="btn btn--ghost"
        (click)="openClearConfirm()"
        [disabled]="isSubmitting || isLoadingSession || !isFormDirty"
        title="Clear all form fields and uploaded files">
        🗑 Clear All
      </button>

      <!-- Upload & Continue -->
      <button
        type="submit"
        class="btn btn--primary"
        [disabled]="!isFormReady"
        [class.btn--loading]="isSubmitting"
        [title]="!isFormReady ? 'Please fill all mandatory fields before continuing' : ''">
        <span *ngIf="!isSubmitting">Upload &amp; Continue &rarr;</span>
        <span *ngIf="isSubmitting" class="btn__spinner" aria-live="polite">
          <ng-container *ngIf="isEditMode">Saving changes&hellip;</ng-container>
          <ng-container *ngIf="!isEditMode">Uploading&hellip;</ng-container>
        </span>
      </button>

    </div>

  </form>

  <!-- ── Clear All confirmation modal ──────────────────────────────────── -->
  <div class="confirm-overlay" *ngIf="showClearConfirm" role="dialog"
       aria-modal="true" aria-labelledby="clearDialogTitle">
    <div class="confirm-dialog">

      <!-- Icon -->
      <div class="confirm-dialog__icon-wrap">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
             class="confirm-dialog__icon" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="#E6192F" stroke-width="1.5" opacity=".25"/>
          <circle cx="12" cy="12" r="8"  stroke="#E6192F" stroke-width="1.5"/>
          <path d="M12 8v5" stroke="#E6192F" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="15.5" r="1" fill="#E6192F"/>
        </svg>
      </div>

      <h2 class="confirm-dialog__title" id="clearDialogTitle">
        Clear All Fields?
      </h2>
      <p class="confirm-dialog__msg">
        This will permanently remove all form data, uploaded files, and CARR links
        from the current session. <strong>This action cannot be undone.</strong>
      </p>

      <div class="confirm-dialog__actions">
        <button type="button" class="btn btn--ghost"
                (click)="cancelClearConfirm()">
          Cancel
        </button>
        <button type="button" class="btn btn--danger"
                (click)="confirmClearAll()">
          Yes, Clear Everything
        </button>
      </div>
    </div>
  </div>

  <!-- ── Success toast notification ─────────────────────────────────────── -->
  <div
    class="upload-toast"
    [class.upload-toast--visible]="showSuccessToast"
    role="status"
    aria-live="polite">
    <svg class="upload-toast__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5z" clip-rule="evenodd"/>
    </svg>
    <span class="upload-toast__message">{{ successMessage }}</span>
    <button type="button" class="upload-toast__close" (click)="dismissToast()" aria-label="Dismiss notification">&times;</button>
  </div>

</div>
